import WebSocketCore from './modules/core';
import ConnectionMonitor from './modules/connectionMonitor';
import PingChannel from './modules/pingChannel';
import WidgetChannel from './modules/widgetChannel';
import AttendantChannel from './modules/attendantChannel';
import BotConfigurationChannel from './modules/botConfigurationChannel';
import SimulatorChannel from './modules/simulatorChannel';
import TokenUsageChannel from './modules/tokenUsageChannel';
import ApiFallback from './modules/apiFallback';

/**
 * ChatWebSocketService
 * Main WebSocket service that integrates all modules
 */
class ChatWebSocketService {
  constructor() {
    // Initialize core service
    this.core = new WebSocketCore();
    
    // Initialize connection monitor
    this.connectionMonitor = new ConnectionMonitor(this.core);
    
    // Initialize channel modules
    this.pingChannel = new PingChannel(this.core);
    this.widgetChannel = new WidgetChannel(this.core);
    this.attendantChannel = new AttendantChannel(this.core, this.connectionMonitor);
    this.botConfigurationChannel = new BotConfigurationChannel(this.core);
    this.simulatorChannel = new SimulatorChannel(this.core);
    this.tokenUsageChannel = new TokenUsageChannel(this.core);

    // Initialize API fallback
    this.apiFallback = new ApiFallback();

    // Initialize handlers map
    this.handlersMap = {
      widget: {},
      attendant: {},
      botConfiguration: {},
      simulator: {},
      tokenUsage: {}
    };
  }

  /**
   * Initialize the WebSocket service
   * @param {boolean} forceReconnect - Whether to force a reconnection if already connected
   */
  initialize(forceReconnect = false) {
    this.core.initialize(forceReconnect);
    
    // Start monitoring the connection
    setTimeout(() => {
      this.connectionMonitor.startMonitoring({
        onReconnectAttendant: (partnerId, attendantId, token) => {
          this.connectAttendant(partnerId, attendantId, token, this.handlersMap.attendant);
        }
      });
    }, 1000);
  }

  /**
   * Connect to the PingChannel
   * @returns {Object|null} The subscription object or null if connection failed
   */
  connectPingChannel() {
    return this.pingChannel.connect();
  }

  /**
   * Connect to the WidgetChannel
   * @param {string} sessionId - The widget session ID
   * @param {Object} handlers - Event handlers for the widget channel
   * @returns {Object|null} The subscription object or null if connection failed
   */
  connectWidget(sessionId, handlers = {}) {
    // Store handlers
    this.handlersMap.widget = handlers || {};
    
    return this.widgetChannel.connect(sessionId, handlers);
  }

  /**
   * Disconnect from the WidgetChannel
   */
  disconnectWidget() {
    this.widgetChannel.disconnect();
  }

  /**
   * Connect to the AttendantChannel
   * @param {string} partnerId - The partner ID
   * @param {string} attendantId - The attendant ID
   * @param {string} token - The authentication token
   * @param {Object} handlers - Event handlers for the attendant channel
   * @returns {Object|null} The subscription object or null if connection failed
   */
  connectAttendant(partnerId, attendantId, token, handlers = {}) {
    // In development environment with no token, try connecting to ping channel first
    // as a way to verify basic WebSocket connectivity
    if (window.location.hostname === 'localhost') {
      console.log('WEBSOCKET INFO: Testing WebSocket connection with PingChannel first');
      this.connectPingChannel();
    }
    
    // Store handlers
    this.handlersMap.attendant = handlers || {};
    
    return this.attendantChannel.connect(partnerId, attendantId, token, handlers);
  }

  /**
   * Disconnect from the AttendantChannel
   */
  disconnectAttendant() {
    this.attendantChannel.disconnect();
  }

  /**
   * Accept a takeover
   * @param {string} takeoverId - The takeover ID
   * @param {string} attendantId - The attendant ID
   * @returns {Promise} A promise that resolves when the takeover is accepted
   */
  acceptTakeover(takeoverId, attendantId) {
    console.log("TAKEOVER DEBUG: Accepting takeover", takeoverId, attendantId);
    
    try {
      // Try to accept via WebSocket first
      return this.attendantChannel.acceptTakeover(takeoverId, attendantId)
        .catch(error => {
          // If WebSocket fails, fall back to API
          console.log("TAKEOVER ERROR: WebSocket takeover failed, falling back to API:", error);
          return this.apiFallback.acceptTakeover(takeoverId, attendantId);
        });
    } catch (error) {
      console.error("TAKEOVER ERROR: Exception during acceptTakeover:", error);
      // Fall back to API
      return this.apiFallback.acceptTakeover(takeoverId, attendantId);
    }
  }

  /**
   * Send a message
   * @param {string} takeoverId - The takeover ID
   * @param {string} attendantId - The attendant ID
   * @param {string} content - The message content
   * @returns {Promise} A promise that resolves when the message is sent
   */
  sendAttendantMessage(takeoverId, attendantId, content) {
    console.log("TAKEOVER DEBUG: Sending message to chat", takeoverId, attendantId);
    
    try {
      // Try to send via WebSocket first
      return this.attendantChannel.sendMessage(takeoverId, attendantId, content)
        .catch(error => {
          // If WebSocket fails, fall back to API
          console.log("TAKEOVER ERROR: WebSocket message failed, falling back to API:", error);
          return this.apiFallback.sendAttendantMessage(takeoverId, attendantId, content);
        });
    } catch (error) {
      console.error("TAKEOVER ERROR: Exception during sendMessage:", error);
      // Fall back to API
      return this.apiFallback.sendAttendantMessage(takeoverId, attendantId, content);
    }
  }

  /**
   * Connect to the BotConfigurationChannel
   * @param {string} partnerId - The partner ID
   * @param {string|null} assistantId - The assistant ID (optional, scopes broadcasts to a specific assistant)
   * @param {Object} handlers - Event handlers for bot configuration updates
   * @returns {Object|null} The subscription object or null if connection failed
   */
  connectBotConfiguration(partnerId, assistantId = null, handlers = {}) {
    // Store handlers
    this.handlersMap.botConfiguration = handlers || {};

    return this.botConfigurationChannel.connect(partnerId, assistantId, handlers);
  }

  /**
   * Disconnect from the BotConfigurationChannel
   */
  disconnectBotConfiguration() {
    this.botConfigurationChannel.disconnect();
  }

  /**
   * Request current bot configuration
   */
  requestBotConfiguration() {
    this.botConfigurationChannel.requestConfiguration();
  }

  /**
   * Connect to the SimulatorChannel
   * @param {string} partnerId - The partner ID
   * @param {string} sessionId - The simulator session ID (optional)
   * @param {string|null} assistantId - The assistant ID (optional, scopes broadcasts to a specific assistant)
   * @param {Object} handlers - Event handlers for simulator updates
   * @returns {Object|null} The subscription object or null if connection failed
   */
  connectSimulator(partnerId, sessionId, assistantId = null, handlers = {}) {
    // Store handlers
    this.handlersMap.simulator = handlers || {};

    return this.simulatorChannel.connect(partnerId, sessionId, assistantId, handlers);
  }

  /**
   * Disconnect from the SimulatorChannel
   */
  disconnectSimulator() {
    this.simulatorChannel.disconnect();
  }

  /**
   * Send a message to the simulator
   * @param {string} message - The message to send
   */
  sendSimulatorMessage(message) {
    this.simulatorChannel.sendMessage(message);
  }

  /**
   * Reset the simulator conversation
   */
  resetSimulatorConversation() {
    this.simulatorChannel.resetConversation();
  }

  /**
   * Request current simulator state
   */
  requestSimulatorState() {
    this.simulatorChannel.requestState();
  }

  /**
   * Connect to the TokenUsageChannel
   * @param {string} partnerId - The partner ID
   * @param {string|null} assistantId - The assistant ID (optional, scopes broadcasts to a specific assistant)
   * @param {Object} handlers - Event handlers for token usage updates
   * @param {Function} handlers.onTokenUsageUpdated - Called when token usage changes
   * @returns {Object|null} The subscription object or null if connection failed
   */
  connectTokenUsage(partnerId, assistantId = null, handlers = {}) {
    // Store handlers
    this.handlersMap.tokenUsage = handlers || {};

    return this.tokenUsageChannel.connect(partnerId, assistantId, handlers);
  }

  /**
   * Disconnect from the TokenUsageChannel
   */
  disconnectTokenUsage() {
    this.tokenUsageChannel.disconnect();
  }

  /**
   * Request current token usage
   */
  requestTokenUsage() {
    this.tokenUsageChannel.requestCurrentUsage();
  }

  /**
   * Check if token usage channel is connected
   * @returns {boolean}
   */
  isTokenUsageConnected() {
    return this.tokenUsageChannel.isConnected();
  }
}

// Create a singleton instance
const chatWebSocketService = new ChatWebSocketService();

export default chatWebSocketService;
